// Which parts of a unit wear its owner's colour.
//
// The renderer repaints a `team` voxel in the owner's colour and keeps its shading from the
// voxel's own lightness, so team voxels are authored as greys. Everything else keeps its true
// palette (a green orc stays green under a blue player). A unit's identity should live in what
// it wears — a sorcerer's robe and staff gem, a warrior's helm crest, belt and boot cuffs — not
// in a wash over the whole body.
//
// This pass picks those regions by role, per family, and by a few per-unit overrides. It runs
// after the detail pass and only on prefabs with no team voxels of their own (a hand-authored
// tabard or banner is left exactly as drawn). It repaints existing voxels; none are added.
import type { BlockVoxel, CharacterPart } from './types.js';

type Family = 'armoured' | 'leather' | 'robed' | 'undead' | 'beast';

/** Parts that are cloth worn over the body: always team-coloured. */
const CLOTH_PART =
  /robe|cloak|cape|tabard|surcoat|skirt|sash|banner|caparison|scarf|stole|mantle|hem|trappings|barding/i;
/** The business end of a staff, wand or banner pole. */
const FOCUS_PART = /staff|wand|scepter|crozier|orb|gem|crystal|pennant|flag/i;
const HEAD_GEAR = /hat|hood|helm|crown|cap|mitre|hair/i;

/** Below this share of team voxels a prefab is treated as having none worth keeping. */
const SPARSE = 0.08;

/** Luminance 0..1 of a #rrggbb colour. */
function lum(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

/** A team voxel's grey: the old brightness, lifted so every owner colour reads. */
function teamGrey(hex: string): string {
  const g = Math.round(255 * Math.max(0.42, Math.min(0.92, 0.3 + lum(hex) * 0.7)));
  const h = g.toString(16).padStart(2, '0');
  return `#${h}${h}${h}`;
}

const toTeam = (v: BlockVoxel): BlockVoxel =>
  // Metal (a buckle, a steel boot) keeps its sheen; glowing voxels keep their glow.
  v.team || v.emissive || (v.metalness ?? 0) >= 0.5
    ? v
    : { ...v, color: teamGrey(v.color), team: true };

const FAMILY_OVERRIDE: Readonly<Record<string, 'wholeBody' | 'headband' | 'loin'>> = {
  zombie: 'loin',
  skeleton: 'loin',
  skeleton_archer: 'loin',
  goblin: 'headband',
  orc: 'loin',
  ogre: 'loin',
  orc_wolf_rider: 'loin',
  bandit: 'headband',
};

/** Where on a part a plan entry paints: a height band, the top layer, or the outer columns (wing edges). */
interface Region {
  readonly part: RegExp;
  readonly from?: number;
  readonly to?: number;
  readonly top?: boolean;
  readonly edge?: number;
}

/** Hand-picked team regions for units whose parts are named their own way. */
const PLAN: Readonly<Record<string, readonly Region[]>> = {
  blood_priest: [{ part: /^robe$/, from: 0, to: 0.7 }, { part: /^hood$/ }, { part: /stole/ }],
  dragon: [
    { part: /wing/, edge: 2 },
    { part: /^head$/, top: true },
  ],
  demon: [{ part: /torso/, from: 0, to: 0.28 }],
  seraphim: [{ part: /^robe$/, from: 0, to: 0.2 }],
  luminarch: [
    { part: /robe_skirt/, from: 0, to: 0.35 },
    { part: /helm/, top: true },
    { part: /shield/ },
  ],
  wagon: [{ part: /cover/ }],
  treant: [],
};

function planned(regions: readonly Region[], part: CharacterPart): CharacterPart {
  const hit = regions.filter((r) => r.part.test(part.name));
  if (hit.length === 0) return part;
  const vs = part.voxels;
  const ys = vs.map((v) => v.y);
  const top = Math.max(...ys);
  const xs = vs.map((v) => Math.abs(v.x));
  const edgeX = Math.max(...xs);
  const picks = hit.map((r) => {
    const band = r.from !== undefined ? layerRange(vs, r.from, r.to ?? 1) : null;
    return (v: BlockVoxel) =>
      (r.top && v.y === top) ||
      (band?.has(v.y) ?? false) ||
      (r.edge !== undefined && Math.abs(v.x) >= edgeX - (r.edge - 1)) ||
      (r.from === undefined && !r.top && r.edge === undefined);
  });
  return { ...part, voxels: vs.map((v) => (picks.some((f) => f(v)) ? toTeam(v) : v)) };
}

function layerRange(voxels: readonly BlockVoxel[], from: number, to: number): Set<number> {
  const ys = voxels.map((v) => v.y);
  const lo = Math.min(...ys);
  const span = Math.max(...ys) - lo + 1;
  const out = new Set<number>();
  for (let y = lo; y <= Math.max(...ys); y++) {
    const f = (y - lo) / span;
    if (f >= from && f < to) out.add(y);
  }
  return out;
}

function teamPart(id: string, family: Family, part: CharacterPart): CharacterPart {
  const name = part.name;
  const vs = part.voxels;
  if (vs.length === 0) return part;
  // Cloth worn over the body, and the focus of a staff or banner: the whole part.
  if (CLOTH_PART.test(name)) return { ...part, voxels: vs.map(toTeam) };
  if (FOCUS_PART.test(name) && vs.length <= 40) return { ...part, voxels: vs.map(toTeam) };
  const role = FAMILY_OVERRIDE[id];
  const ys = vs.map((v) => v.y);
  const top = Math.max(...ys);
  const bottom = Math.min(...ys);
  let pick: (v: BlockVoxel) => boolean = () => false;
  if (/^(body|torso|chest)$/i.test(name)) {
    if (family === 'robed') {
      // The robe: everything from the waist down, plus the collar.
      const robe = layerRange(vs, 0, 0.72);
      pick = (v) => robe.has(v.y) || v.y === top;
    } else if (role === 'loin') {
      const loin = layerRange(vs, 0, 0.3);
      pick = (v) => loin.has(v.y);
    } else {
      // Belt layer, and a sash/collar for the rest.
      const belt = layerRange(vs, 0.24, 0.4);
      pick = (v) => belt.has(v.y) || v.y === top;
    }
  } else if (/^head$/i.test(name) || HEAD_GEAR.test(name)) {
    if (family === 'armoured') {
      // A crest along the top of the helm.
      const mid = Math.round(
        (Math.min(...vs.map((v) => v.x)) + Math.max(...vs.map((v) => v.x))) / 2,
      );
      pick = (v) => v.y === top && Math.abs(v.x - mid) <= 0 && !v.team;
    } else if (family === 'robed' && HEAD_GEAR.test(name)) {
      pick = () => true; // a wizard's hat or a priest's hood is part of the robe
    } else if (role === 'headband') {
      const band = layerRange(vs, 0.55, 0.78);
      pick = (v) => band.has(v.y);
    } else if (family === 'leather' && HEAD_GEAR.test(name)) {
      pick = (v) => v.y === bottom + 1 || v.y === bottom; // hat band
    }
  } else if (/leg/i.test(name) && (family === 'armoured' || family === 'leather')) {
    pick = (v) => v.y === bottom + 2; // the boot cuff
  } else if (/arm/i.test(name) && role === 'loin') {
    pick = (v) => v.y === bottom + 1; // wristbands
  } else if (/arm/i.test(name) && family === 'robed') {
    const sleeve = layerRange(vs, 0, 0.45);
    pick = (v) => sleeve.has(v.y); // the sleeves' cuffs
  }
  return { ...part, voxels: vs.map((v) => (pick(v) ? toTeam(v) : v)) };
}

/** The parts with their team regions marked; unchanged when the prefab already has team voxels. */
export function withTeamParts(
  id: string,
  family: Family | undefined,
  parts: readonly CharacterPart[],
): CharacterPart[] {
  const all = parts.flatMap((p) => p.voxels);
  // A prefab that already draws a real share of team colour keeps its authored design; a few
  // token voxels (a stole, a small cape) do not count and are built on.
  if (!family || all.filter((v) => v.team).length / Math.max(1, all.length) >= SPARSE)
    return [...parts];
  const plan = PLAN[id];
  if (plan) return parts.map((p) => planned(plan, p));
  return parts.map((p) => teamPart(id, family, p));
}
